import { DOCUMENT } from '@angular/common';
import {
    afterNextRender,
    Component,
    ElementRef,
    inject,
    input,
    OnDestroy,
    signal,
    ChangeDetectionStrategy
} from '@angular/core';

type VerticalAlignment = 'top' | 'center' | 'bottom';
type HorizontalAlignment = 'left' | 'center' | 'right';
type CurveStyle = 'default' | 'vector' | 'left' | 'right' | 'right-left';

@Component({
    selector: 'app-home-connector',
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './home-connector.component.html'
})
export class HomeConnectorComponent implements OnDestroy {
    private readonly document = inject(DOCUMENT);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    protected readonly path = signal('');
    protected readonly startPoint = signal({ x: 0, y: 0 });
    protected readonly endPoint = signal({ x: 0, y: 0 });
    private readonly isMobile = signal(false);
    private resizeObserver?: ResizeObserver;
    private mediaQuery?: MediaQueryList;

    public readonly startAnchor = input.required<string>();
    public readonly endAnchor = input.required<string>();
    public readonly mobileStartAnchor = input<string>();
    public readonly mobileEndAnchor = input<string>();
    public readonly startVertical = input<VerticalAlignment>('bottom');
    public readonly startHorizontal = input<HorizontalAlignment>('right');
    public readonly endVertical = input<VerticalAlignment>('top');
    public readonly endHorizontal = input<HorizontalAlignment>('left');
    public readonly mobileStartVertical = input<VerticalAlignment>();
    public readonly mobileStartHorizontal = input<HorizontalAlignment>();
    public readonly mobileEndVertical = input<VerticalAlignment>();
    public readonly mobileEndHorizontal = input<HorizontalAlignment>();
    public readonly curveScale = input(0.7);
    public readonly curveStyle = input<CurveStyle>('default');

    public constructor() {
        afterNextRender(() => {
            this.mediaQuery = window.matchMedia('(max-width: 1023px)');
            this.isMobile.set(this.mediaQuery.matches);
            this.mediaQuery.addEventListener('change', this.handleBreakpointChange);
            this.updatePath();
            this.resizeObserver = new ResizeObserver(() => this.updatePath());
            const parent = this.elementRef.nativeElement.parentElement;

            if (parent) {
                this.resizeObserver.observe(parent);
            }

            this.observeAnchors();
        });
    }

    public ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.mediaQuery?.removeEventListener('change', this.handleBreakpointChange);
    }

    private updatePath(): void {
        const host = this.elementRef.nativeElement;
        const parent = host.parentElement;
        const start = this.getAnchor(this.getActiveValue(this.startAnchor, this.mobileStartAnchor));
        const end = this.getAnchor(this.getActiveValue(this.endAnchor, this.mobileEndAnchor));

        if (!parent || !start || !end) {
            return;
        }

        const parentRect = parent.getBoundingClientRect();
        const startRect = start.getBoundingClientRect();
        const endRect = end.getBoundingClientRect();
        const startPoint = this.getPoint(
            startRect,
            this.getActiveValue(this.startHorizontal, this.mobileStartHorizontal),
            this.getActiveValue(this.startVertical, this.mobileStartVertical),
            parentRect
        );
        const endPoint = this.getPoint(
            endRect,
            this.getActiveValue(this.endHorizontal, this.mobileEndHorizontal),
            this.getActiveValue(this.endVertical, this.mobileEndVertical),
            parentRect
        );
        const { x: startX, y: startY } = startPoint;
        const { x: endX, y: endY } = endPoint;

        const horizontalArc = Math.max(Math.abs(endX - startX) * this.curveScale(), 120);

        this.startPoint.set({ x: startX, y: startY });
        this.endPoint.set({ x: endX, y: endY });
        this.path.set(this.getCurvePath(startX, startY, endX, endY, horizontalArc));
    }

    private getActiveValue<T>(desktopValue: () => T, mobileValue: () => T | undefined): T {
        return this.isMobile() ? (mobileValue() ?? desktopValue()) : desktopValue();
    }

    private getAnchor(id: string): HTMLElement | null {
        return this.document.getElementById(id);
    }

    private observeAnchors(): void {
        if (!this.resizeObserver) {
            return;
        }

        [this.startAnchor(), this.endAnchor(), this.mobileStartAnchor(), this.mobileEndAnchor()]
            .filter((anchor): anchor is string => Boolean(anchor))
            .map(anchor => this.getAnchor(anchor))
            .filter((element): element is HTMLElement => Boolean(element))
            .forEach(element => this.resizeObserver?.observe(element));
    }

    private readonly handleBreakpointChange = (event: MediaQueryListEvent): void => {
        this.isMobile.set(event.matches);
        this.updatePath();
    };

    private getVerticalPosition(rect: DOMRect, vertical: VerticalAlignment, parentTop: number): number {
        switch (vertical) {
            case 'bottom':
                return rect.bottom - parentTop + 12;
            case 'center':
                return rect.top + rect.height / 2 - parentTop;
            case 'top':
                return rect.top - parentTop - 12;
        }
    }

    private getHorizontalPosition(rect: DOMRect, horizontal: HorizontalAlignment, parentLeft: number): number {
        switch (horizontal) {
            case 'center':
                return rect.left + rect.width / 2 - parentLeft;
            case 'right':
                return rect.right - parentLeft + 12;
            case 'left':
                return rect.left - parentLeft - 12;
        }
    }

    private getPoint(
        rect: DOMRect,
        horizontal: HorizontalAlignment,
        vertical: VerticalAlignment,
        parentRect: DOMRect
    ): { x: number; y: number } {
        return {
            x: this.getHorizontalPosition(rect, horizontal, parentRect.left),
            y: this.getVerticalPosition(rect, vertical, parentRect.top)
        };
    }

    private getCurvePath(startX: number, startY: number, endX: number, endY: number, horizontalArc: number): string {
        const style = this.curveStyle();

        switch (style) {
            case 'vector':
                return this.getVectorPath(startX, startY, endX, endY);
            case 'right-left':
                return this.getRightLeftPath(startX, startY, endX, endY);
            case 'left':
                return this.getLeftPath(startX, startY, endX, endY, horizontalArc);
            case 'right':
                return this.getRightPath(startX, startY, endX, endY, horizontalArc);
            case 'default':
                return `M ${startX} ${startY} C ${startX + horizontalArc} ${startY}, ${endX - horizontalArc} ${endY}, ${endX} ${endY}`;
        }
    }

    private getVectorPath(startX: number, startY: number, endX: number, endY: number): string {
        const width = Math.max(Math.abs(startX - endX), 280);
        const height = Math.max(Math.abs(endY - startY), 180);
        const direction = endX < startX ? 1 : -1;
        const middleX = startX - direction * width * 0.16;
        const middleY = startY + height * 0.55;
        const tangentX = width * 0.3;
        const tangentY = height * 0.28;

        return (
            `M ${startX} ${startY} ` +
            `C ${startX + direction * width * 0.4} ${startY}, ${middleX + direction * tangentX} ${middleY - tangentY}, ${middleX} ${middleY} ` +
            `C ${middleX - direction * tangentX} ${middleY + tangentY}, ${endX + direction * width * 0.4} ${endY - height * 0.3}, ${endX} ${endY}`
        );
    }

    private getRightLeftPath(startX: number, startY: number, endX: number, endY: number): string {
        const width = Math.max(Math.abs(endX - startX), 260);
        const rightArc = Math.max(Math.abs(endX - startX) * 0.8, 220);
        const height = Math.max(Math.abs(endY - startY), 180);
        const leftSwing = Math.min(width * 0.72, Math.max(width * 0.3, 180));
        const firstControlX = startX + rightArc;
        const secondControlX = Math.max(endX - leftSwing, 24);

        return `M ${startX} ${startY} C ${firstControlX} ${startY + height * 0.24}, ${secondControlX} ${endY - height * 0.24}, ${endX} ${endY}`;
    }

    private getRightPath(startX: number, startY: number, endX: number, endY: number, horizontalArc: number): string {
        return `M ${startX} ${startY} C ${startX + horizontalArc} ${startY}, ${startX + horizontalArc} ${endY}, ${endX} ${endY}`;
    }

    private getLeftPath(startX: number, startY: number, endX: number, endY: number, horizontalArc: number): string {
        return `M ${startX} ${startY} C ${startX - horizontalArc} ${startY}, ${startX - horizontalArc} ${endY}, ${endX} ${endY}`;
    }
}
